import { useCallback, useEffect, useState } from 'react';
import SettingsDrawer from '@/core/settings/components/settings-drawer';
import {
  NotificationsSubTab,
  registerSettingsHandler,
  SettingsTab,
} from '@/core/widget-navigation/export';

const HASH_PREFIX = 'settings';
const DEFAULT_TAB: SettingsTab = 'connection';
const DEFAULT_SUBTAB: NotificationsSubTab = 'subscriptions';

interface SettingsHashState {
  isOpen: boolean;
  tab: SettingsTab;
  subtab: NotificationsSubTab;
}

function parseSettingsHash(): SettingsHashState {
  const hash = window.location.hash.replace(/^#/, '');
  const segments = hash.split(':');

  if (segments[0] !== HASH_PREFIX)
    return { isOpen: false, tab: DEFAULT_TAB, subtab: DEFAULT_SUBTAB };

  return {
    isOpen: true,
    tab: (segments[1] as SettingsTab) || DEFAULT_TAB,
    subtab: (segments[2] as NotificationsSubTab) || DEFAULT_SUBTAB,
  };
}

function writeSettingsHash(state: SettingsHashState) {
  const currentHash = window.location.hash.replace(/^#/, '');

  if (!state.isOpen) {
    if (!currentHash.startsWith(`${HASH_PREFIX}:`)) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return;
  }

  const target = `#${HASH_PREFIX}:${state.tab}:${state.subtab}`;
  window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
}

// Mounted once at the App root (sibling of RouterProvider), same as
// SidePanelShell — an overlay drawer that sits on top of whatever route is
// currently showing, rather than a route of its own, so the page underneath
// stays visible/dimmed behind the backdrop.
export default function SettingsDrawerShell() {
  const [initial] = useState(() => parseSettingsHash());
  const [isOpen, setIsOpen] = useState(initial.isOpen);
  const [tab, setTab] = useState<SettingsTab>(initial.tab);
  const [subtab, setSubtab] = useState<NotificationsSubTab>(initial.subtab);

  useEffect(() => {
    writeSettingsHash({ isOpen, tab, subtab });
  }, [isOpen, tab, subtab]);

  // hashchange only fires on a real navigation (address-bar edit/paste, a
  // link) — never from writeSettingsHash above, which uses replaceState.
  useEffect(() => {
    const onHashChange = () => {
      const parsed = parseSettingsHash();
      setIsOpen(parsed.isOpen);
      setTab(parsed.tab);
      setSubtab(parsed.subtab);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    return registerSettingsHandler((openTab, openSubtab) => {
      setTab(openTab ?? DEFAULT_TAB);
      setSubtab(openSubtab ?? DEFAULT_SUBTAB);
      setIsOpen(true);
    });
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <SettingsDrawer
      isOpen={isOpen}
      tab={tab}
      subtab={subtab}
      onClose={close}
      onTabChange={setTab}
      onSubtabChange={setSubtab}
    />
  );
}
